import { Form, FormProps, Input, Modal, ModalProps, Typography } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import ICurrency from '../../../pages/currencies/currency.interface';
import { AutoComplete } from '../../inputs/autocomplete';
import { IBank } from '../../../interfaces/banks';
import { ButtonListFooterModalSM } from '../../../pages/components/buttonsList';
import { IBankAccount } from '../../../interfaces/bankAccounts';
import {
  abaRoutingNumberRules,
  accountNumberRules,
  bankStateBranchNumberRules,
  branchNumberRules,
  ibanRules,
  intermediaryAccountNumberRules,
  isBankAccountFieldOptional,
  isIbanRequiredByCountryAndCurrency,
} from '../../../pages/payment-request/addAccount.constants';
import { ICompany } from '../../../interfaces/companies';
import {
  bankRulesSimple,
  companyRules,
  companySearchFieldNameFilter,
  currencyRulesSimple,
} from '../../../utils/form.constants';
import { intermediaryBankNameRules, intermediarySwiftRules } from '../../../pages/payment-request/addBank.constants';
import { useNotificationOkCancel } from '../../../utils/hooks/useNotificationOkCancel.hook';
import { confirmModalUnsavedChangesProps } from '../../../pages/components/constants';
import { Option } from '../../inputs/autocomplete/autocomplete';
import { mapBankItem, mapCompanyItem, mapCurrencyItem } from '../../../utils/edit.constants';
import { DownOutlined } from '@ant-design/icons';

interface IBankAccountModalCreateSubmit {
  bankId?: number;
  currencyCode?: string;
  companyId?: number;
  correspondentBank?: string;
  correspondentBankSwift?: string;
  correspondentBankAccountNumber?: string;
  accountNumber: string;
}

interface IProps {
  formProps: FormProps;
  modalProps: ModalProps;
  formIsLoading: boolean;
  formSubmit: (data: IBankAccountModalCreateSubmit) => void;
  closeModal: () => void;
}

export const NewBankAccountModalCreate = ({ modalProps, formProps, formIsLoading, formSubmit, closeModal }: IProps) => {
  const [currencyData, setCurrencyData] = useState<ICurrency>();
  const [bankData, setBankData] = useState<IBank>();
  const [companyData, setCompanyData] = useState<ICompany>();

  const [form] = Form.useForm();

  const onSubmit = useCallback(
    (formValues: IBankAccount) => {
      const data = {
        bankId: bankData?.id,
        currencyCode: currencyData?.code,
        companyId: companyData?.id,
        correspondentBank: formValues.correspondentBank,
        correspondentBankSwift: formValues.correspondentBankSwift,
        correspondentBankAccountNumber: formValues.correspondentBankAccountNumber,
        accountNumber: formValues.accountNumber,
        abaRoutingNumber: formValues.abaRoutingNumber,
        bankStateBranchNumber: formValues.bankStateBranchNumber,
        branchNumber: formValues.branchNumber,
        iban: formValues.iban,
      };
      formSubmit(data);
      form.resetFields();
      setCurrencyData(undefined);
      setBankData(undefined);
      closeModal();
    },
    [bankData?.id, currencyData?.code, companyData?.id, formSubmit, form, closeModal],
  );

  const handleSubmit = useCallback(() => {
    form.submit();
  }, [form]);

  const { openNotification, contextHolder } = useNotificationOkCancel({
    ...confirmModalUnsavedChangesProps,
    onOk: closeModal,
  });

  const clearCurrencyData = useCallback(() => {
    setCurrencyData(undefined);
  }, []);

  const changeBankData = useCallback(
    (data: IBank) => {
      if (data.id !== bankData?.id) {
        setBankData(data);
      }
    },
    [bankData?.id],
  );

  const onSelectBank = useCallback(
    (_: IBank, { data }: Option<IBank>) => {
      return changeBankData(data);
    },
    [changeBankData],
  );

  const clearBankData = useCallback(() => {
    setBankData(undefined);
  }, []);

  const changeCompanyData = useCallback(
    (data: ICompany) => {
      if (data.id !== companyData?.id || data.id === undefined) {
        setCompanyData(data);
      }
    },
    [companyData?.id],
  );

  const onSelectCompany = useCallback(
    (_: ICompany, { data }: Option<ICompany>) => {
      changeCompanyData(data);
    },
    [changeCompanyData],
  );

  const clearCompanyData = useCallback(() => {
    setCompanyData(undefined);
  }, []);

  const onModalCancel = useCallback(() => {
    const formIsNotEmpty = Object.values(form.getFieldsValue()).some(elm => elm !== undefined);
    if (formIsNotEmpty) {
      openNotification();
    } else {
      closeModal();
    }
  }, [openNotification, closeModal, form]);

  const afterClose = useCallback(() => {
    setCurrencyData(undefined);
    setBankData(undefined);
    setCompanyData(undefined);
    form.resetFields();
    closeModal();
  }, [closeModal, form]);

  useEffect(() => {
    if (!bankData) {
      form.resetFields(['abaRoutingNumber', 'bankStateBranchNumber', 'branchNumber', 'iban']);
    } else {
      if (bankData.country === 'US') {
        form.resetFields(['bankStateBranchNumber', 'branchNumber', 'iban']);
      } else if (bankData.country === 'AU') {
        form.resetFields(['abaRoutingNumber', 'branchNumber', 'iban']);
      } else if (isBankAccountFieldOptional(bankData.country, currencyData?.code)) {
        form.resetFields(['abaRoutingNumber', 'bankStateBranchNumber', 'branchNumber', 'iban']);
      } else {
        form.resetFields(['abaRoutingNumber', 'bankStateBranchNumber']);
      }
    }
  }, [bankData, currencyData, form]);

  const numberField = useMemo(() => {
    return (
      <>
        <Form.Item name='abaRoutingNumber' rules={abaRoutingNumberRules(bankData?.country)}>
          <Input
            placeholder='ABA routing number'
            style={{
              height: '35px',
            }}
            disabled={bankData?.country !== 'US'}
          />
        </Form.Item>
        <div style={{ marginTop: '-16px', display: 'flex', justifyContent: 'start', marginBottom: '16px' }}>
          <Typography.Text
            style={{
              color: '#F28A28',
              fontSize: 12,
              fontWeight: 400,
              lineHeight: '16px',
            }}>
            {
              'If not available, please key in 000000.'
            }
          </Typography.Text>
        </div>
        <Form.Item name='bankStateBranchNumber' rules={bankStateBranchNumberRules(bankData?.country)}>
          <Input
            placeholder='Bank State Branch number'
            style={{
              height: '35px',
            }}
            disabled={bankData?.country !== 'AU'}
          />
        </Form.Item>
        <div style={{ marginTop: '-16px', display: 'flex', justifyContent: 'start', marginBottom: '16px' }}>
          <Typography.Text
            style={{
              color: '#F28A28',
              fontSize: 12,
              fontWeight: 400,
              lineHeight: '16px',
            }}>
            {
              'If not available, please key in 000000.'
            }
          </Typography.Text>
        </div>
        <Form.Item name='branchNumber' rules={branchNumberRules(bankData?.country, currencyData?.code)}>
          <Input
            placeholder='Branch number'
            style={{
              height: '35px',
            }}
            disabled={
              bankData?.country === 'AU' ||
              bankData?.country === 'US' ||
              isBankAccountFieldOptional(bankData?.country, currencyData?.code) ||
              isIbanRequiredByCountryAndCurrency(bankData?.country, currencyData?.code)
            }
          />
        </Form.Item>
        <div style={{ marginTop: '-16px', display: 'flex', justifyContent: 'start', marginBottom: '16px' }}>
          <Typography.Text
            style={{
              color: '#F28A28',
              fontSize: 12,
              fontWeight: 400,
              lineHeight: '16px',
            }}>
            {
              'If not available, please key in 000000.'
            }
          </Typography.Text>
        </div>
      </>
    );
  }, [bankData, currencyData]);

  const ibanField = useMemo(
    () => (
      <Form.Item name='iban' rules={ibanRules(bankData?.country, currencyData?.code)}>
        <Input
          placeholder='IBAN'
          style={{
            height: '35px',
          }}
          disabled={!isIbanRequiredByCountryAndCurrency(bankData?.country, currencyData?.code)}
        />
      </Form.Item>
    ),
    [bankData, currencyData],
  );

  return (
    <>
      <Modal
        {...modalProps}
        title='Add new account'
        className='smartModalSM'
        footer={ButtonListFooterModalSM(handleSubmit, onModalCancel, formIsLoading)}
        onCancel={onModalCancel}
        afterClose={afterClose}
      >
        {contextHolder}
        <Form {...formProps} layout='vertical' onFinish={onSubmit} disabled={formIsLoading} form={form}>
          <Form.Item name='bankId' rules={bankRulesSimple(bankData)}>
            <AutoComplete
              resource='api/v1/banks'
              mapItem={mapBankItem}
              onSelect={onSelectBank}
              allowClear
              refetchOnEmptyValue
              onClear={clearBankData}
            >
              <Input placeholder='Bank' size='large' suffix={<DownOutlined />} />
            </AutoComplete>
          </Form.Item>
          <Form.Item
            name='currencyCode'
            rules={currencyRulesSimple(currencyData)}
            style={{
              height: '35px',
            }}
          >
            <AutoComplete
              allowClear
              resource='api/v1/currencies'
              mapItem={mapCurrencyItem}
              onSelect={(_, { data }) => setCurrencyData(data)}
              onClear={clearCurrencyData}
              refetchOnEmptyValue
            >
              <Input placeholder='Currency' size='large' suffix={<DownOutlined />} />
            </AutoComplete>
          </Form.Item>
          <Form.Item name='accountNumber' rules={accountNumberRules}>
            <Input
              placeholder='Account number'
              style={{
                height: '35px',
                marginTop: '15px',
              }}
            />
          </Form.Item>
          <Form.Item name='companyId' rules={companyRules(companyData)}>
            <AutoComplete
              resource='api/v1/companies'
              additionalFilters={[companySearchFieldNameFilter]}
              mapItem={mapCompanyItem}
              onSelect={onSelectCompany}
              allowClear
              refetchOnEmptyValue
              onClear={clearCompanyData}
            >
              <Input
                placeholder='Provider'
                size='large'
                style={{
                  height: '35px',
                  marginTop: '-2px',
                }}
                suffix={<DownOutlined />}
              />
            </AutoComplete>
          </Form.Item>
          {numberField}
          {ibanField}
          <Form.Item name='correspondentBank' rules={intermediaryBankNameRules}>
            <Input
              placeholder='Intermediary bank'
              style={{
                height: '35px',
              }}
            />
          </Form.Item>
          <Form.Item name='correspondentBankSwift' rules={intermediarySwiftRules}>
            <Input
              placeholder='Intermediary bank SWIFT'
              style={{
                height: '35px',
              }}
            />
          </Form.Item>
          <Form.Item name='correspondentBankAccountNumber' rules={intermediaryAccountNumberRules}>
            <Input
              placeholder='Intermediary bank account'
              style={{
                height: '35px',
              }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
